import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CircleAlert, CircleCheck, KeyRound, Pencil, Plus, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { LoadingState } from '@/components/ui/states';
import { Table, TableWrap, THead, TH, TR, TD } from '@/components/ui/table';
import ProfileFormDialog from './ProfileFormDialog';
import CredentialsDialog from './CredentialsDialog';
import TaxMappingsSection from './TaxMappingsSection';
import ItemMappingsSection from './ItemMappingsSection';
import {
  FISCAL_ROUTES,
  FISCAL_PERMISSIONS,
  FiscalProfileStatus,
  apiMessage,
  type FiscalProfile,
  type FiscalConfigurationValidation,
} from '../fiscalApi';
import { profileStatusTone } from '../lib';

/**
 * Setting a venue up (`docs/fiscal.md` Part 1, steps 1–9).
 *
 * The validation banner is the gate on activation and is deliberately at the
 * TOP of the screen, above the profile list it describes: `ready: true` means
 * there is nothing left, and everything else on this tab exists to make that
 * true. It answers `GET /fiscal/configuration/validation`, which is the one
 * call this epic exists to make possible — finding a missing item code on a
 * Tuesday afternoon, not while a guest is waiting to pay.
 */
export default function ConfigurationPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();

  const [profileDialog, setProfileDialog] = useState<FiscalProfile | 'new' | null>(null);
  const [credentialsFor, setCredentialsFor] = useState<FiscalProfile | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const profiles = useApi<unknown>(can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.profiles : null);
  const { rows: profileRows } = rowsOf<FiscalProfile>(profiles.data);

  const validation = useApi<FiscalConfigurationValidation>(
    can(FISCAL_PERMISSIONS.view) ? FISCAL_ROUTES.validation : null,
  );

  const reload = () => {
    profiles.reload();
    validation.reload();
  };

  const verify = async (profile: FiscalProfile) => {
    setBusyId(profile.id);
    try {
      const { data } = await api.post<{ ok: boolean; issues: { message: string }[] }>(
        FISCAL_ROUTES.verify(profile.id),
      );
      if (data.ok) toast.success(t('fiscal.config.verifyOk'));
      else toast.error(data.issues.map((i) => i.message).join(' · ') || t('fiscal.config.verifyFailed'));
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.verifyFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const activate = async (profile: FiscalProfile) => {
    setBusyId(profile.id);
    try {
      await api.post(FISCAL_ROUTES.activate(profile.id));
      toast.success(t('fiscal.config.activated'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.activateFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const disable = async (profile: FiscalProfile) => {
    const ok = await confirm({
      title: t('fiscal.config.disableTitle'),
      body: t('fiscal.config.disableBody'),
      confirmLabel: t('fiscal.config.disable'),
      tone: 'danger',
    });
    if (!ok) return;
    setBusyId(profile.id);
    try {
      await api.post(FISCAL_ROUTES.disable(profile.id));
      toast.success(t('fiscal.config.disabled'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const setEnvironment = async (profile: FiscalProfile, environment: 'SANDBOX' | 'PRODUCTION') => {
    if (environment === 'PRODUCTION') {
      const ok = await confirm({
        title: t('fiscal.config.goLiveTitle'),
        body: t('fiscal.config.goLiveBody'),
        confirmLabel: t('fiscal.config.goLiveConfirm'),
        tone: 'danger',
      });
      if (!ok) return;
    }
    setBusyId(profile.id);
    try {
      await api.post(FISCAL_ROUTES.environment(profile.id), { environment });
      toast.success(t('fiscal.config.environmentChanged'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.config.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        {validation.loading ? (
          <LoadingState />
        ) : validation.error ? (
          <div role="alert" className="rounded-xl border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
            <p className="font-medium text-red-700 dark:text-red-300">{t('fiscal.states.summaryFailed')}</p>
            <p className="mt-1 text-ink-muted">{validation.error}</p>
          </div>
        ) : validation.data ? (
          <div
            className={`rounded-xl border p-4 text-sm ${
              validation.data.ready
                ? 'border-green-300 bg-green-50/70 dark:border-green-900 dark:bg-green-950/30'
                : 'border-amber-300 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30'
            }`}
          >
            <p className="flex items-center gap-2 font-medium">
              <Icon
                as={validation.data.ready ? CircleCheck : CircleAlert}
                className={validation.data.ready ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}
              />
              {validation.data.ready ? t('fiscal.config.ready') : t('fiscal.config.notReady')}
            </p>
            {!validation.data.ready && (
              <ul className="mt-2 list-inside list-disc space-y-1 text-ink-muted">
                {validation.data.unmappedItemCount > 0 && (
                  <li>{t('fiscal.config.unmappedItems', { count: validation.data.unmappedItemCount })}</li>
                )}
                {validation.data.invalidMappings.length > 0 && (
                  <li>{t('fiscal.config.invalidMappings', { count: validation.data.invalidMappings.length })}</li>
                )}
                {!validation.data.hasDefaultTaxMapping && <li>{t('fiscal.config.noDefaultTax')}</li>}
                {validation.data.profileIssues.map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('fiscal.config.profiles')}</h2>
          {can(FISCAL_PERMISSIONS.configure) && (
            <Button size="sm" onClick={() => setProfileDialog('new')}>
              <Icon as={Plus} />
              {t('fiscal.config.newProfile')}
            </Button>
          )}
        </div>

        <TableWrap>
          {profiles.loading ? (
            <LoadingState />
          ) : profiles.error ? (
            <div role="alert" className="p-4 text-sm text-red-700 dark:text-red-300">
              {profiles.error}
            </div>
          ) : profileRows.length === 0 ? (
            <div className="grid place-items-center gap-2 py-16 text-center">
              <p className="text-sm font-medium">{t('fiscal.config.noProfiles')}</p>
              <p className="max-w-sm text-sm text-ink-muted">{t('fiscal.config.noProfilesHint')}</p>
            </div>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('fiscal.config.columns.regime')}</TH>
                  <TH>{t('fiscal.config.columns.scope')}</TH>
                  <TH>{t('fiscal.config.columns.legalName')}</TH>
                  <TH>{t('fiscal.config.columns.status')}</TH>
                  <TH>{t('fiscal.config.columns.environment')}</TH>
                  <TH />
                </TR>
              </THead>
              <tbody>
                {profileRows.map((profile) => (
                  <TR key={profile.id}>
                    <TD>{t(`fiscal.regime.${profile.regime}`)}</TD>
                    <TD className="text-xs text-ink-muted">
                      {profile.branchId ? profile.branchId : t('fiscal.config.tenantWide')}
                    </TD>
                    <TD>{profile.legalName}</TD>
                    <TD>
                      <Badge tone={profileStatusTone(profile.status)}>{t(`fiscal.profileStatus.${profile.status}`)}</Badge>
                    </TD>
                    <TD>
                      <Badge tone={profile.environment === 'PRODUCTION' ? 'danger' : 'neutral'}>
                        {t(`fiscal.environment.${profile.environment}`)}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => verify(profile)}
                          disabled={busyId === profile.id}
                          title={t('fiscal.config.verify')}
                        >
                          <Icon as={ShieldCheck} size="sm" />
                        </Button>
                        {can(FISCAL_PERMISSIONS.credentials) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCredentialsFor(profile)}
                            title={t('fiscal.config.credentials')}
                          >
                            <Icon as={KeyRound} size="sm" />
                          </Button>
                        )}
                        {can(FISCAL_PERMISSIONS.configure) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setProfileDialog(profile)}
                            title={t('common.edit')}
                          >
                            <Icon as={Pencil} size="sm" />
                          </Button>
                        )}
                        {can(FISCAL_PERMISSIONS.configure) && profile.status === FiscalProfileStatus.DRAFT && (
                          <Button size="sm" variant="outline" onClick={() => activate(profile)} disabled={busyId === profile.id}>
                            {t('fiscal.config.activate')}
                          </Button>
                        )}
                        {can(FISCAL_PERMISSIONS.configure) && profile.status === FiscalProfileStatus.ACTIVE && (
                          <Button size="sm" variant="outline" onClick={() => disable(profile)} disabled={busyId === profile.id}>
                            {t('fiscal.config.disable')}
                          </Button>
                        )}
                        {can(FISCAL_PERMISSIONS.configure) && profile.environment === 'SANDBOX' && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setEnvironment(profile, 'PRODUCTION')}
                            disabled={busyId === profile.id}
                          >
                            {t('fiscal.config.goLive')}
                          </Button>
                        )}
                        {can(FISCAL_PERMISSIONS.configure) && profile.environment === 'PRODUCTION' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEnvironment(profile, 'SANDBOX')}
                            disabled={busyId === profile.id}
                          >
                            {t('fiscal.config.backToSandbox')}
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </TableWrap>
      </section>

      <TaxMappingsSection />
      <ItemMappingsSection />

      <ProfileFormDialog
        target={profileDialog}
        onClose={() => setProfileDialog(null)}
        onSaved={() => {
          setProfileDialog(null);
          reload();
        }}
      />
      <CredentialsDialog profile={credentialsFor} onClose={() => setCredentialsFor(null)} />
    </div>
  );
}
